import { formatPhone } from "@rhema/client";
import {
  Avatar,
  Button,
  Card,
  ChevronLeftIcon,
  cn,
  EmptyState,
  Eyebrow,
  focusRing,
  MessageIcon,
  UiLink,
} from "@rhema/ui";
import { useState } from "react";
import { useNavigate } from "react-router";
import { firstName, presenceText, toneFor } from "../data/view";
import { ReportSheet } from "../people/ReportSheet";
import { SafetyNumbers } from "../people/SafetyNumbers";
import { contactsChanged, usePerson } from "../people/useContacts";
import { useApi, useClient, useClientState } from "../session/session";

// Someone's profile, from the Contacts tab.
export function ContactScreen({ personId, variant }: { personId: string; variant: "phone" | "page" }) {
  const api = useApi();
  const client = useClient();
  const state = useClientState();
  const navigate = useNavigate();
  const { person, error: loadError, reload } = usePerson(personId);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [reported, setReported] = useState(false);

  if (loadError) return <EmptyState title="Contact not found" className="flex-1" />;
  if (!person) return <EmptyState title="Loading" className="flex-1" />;

  const first = firstName(person.display_name);
  // The one-to-one chat, whose recent messages a report can include.
  const direct = Object.values(state.chats).find(
    (c) => c.kind === "direct" && c.members.some((m) => m.user.id === person.id),
  );
  const presence = presenceText(state.presence[person.id]);

  const run = async (job: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await job();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const message = () =>
    run(async () => {
      const chat = await client.createDirect(person.id);
      navigate(`/chats/${chat.id}`);
    });

  const toggleContact = () =>
    run(async () => {
      if (person.is_contact) await api.removeContact(person.id);
      else await api.addContact(person.id);
      reload();
      contactsChanged();
    });

  const toggleBlock = () =>
    run(async () => {
      if (person.is_blocked) await api.unblock(person.id);
      else await api.block(person.id);
      reload();
      contactsChanged();
    });

  return (
    <div
      aria-busy={busy}
      className={cn(
        "flex min-h-0 flex-1 flex-col overflow-y-auto bg-linen",
        variant === "page" && "items-center",
      )}
    >
      <div className={cn("flex w-full flex-col", variant === "page" && "max-w-[560px] py-6")}>
        <div
          className={cn(
            "flex flex-none flex-col items-center bg-surface-header px-4 pt-3 pb-[22px]",
            variant === "page" ? "rounded-sheet" : "rounded-b-sheet",
          )}
        >
          <div className="flex self-stretch justify-between">
            {variant === "phone" && (
              <UiLink
                href="/contacts"
                aria-label="Back to contacts"
                className={cn(
                  "flex h-11 w-11 items-center justify-center rounded-field bg-linen text-ink",
                  focusRing,
                )}
              >
                <ChevronLeftIcon size={22} strokeWidth={2.2} />
              </UiLink>
            )}
          </div>
          <span className="-mt-1.5 rounded-full border-4 border-linen">
            <Avatar
              name={person.display_name}
              src={person.avatar_url}
              tone={toneFor(person.id)}
              size={100}
              serif
              online={presence === "online"}
              ringClassName="border-linen"
            />
          </span>
          <h1 className="m-0 mt-3 text-center font-serif text-[30px] font-medium tracking-[-0.02em]">
            {person.display_name}
          </h1>
          <p className="m-0 mt-0.5 text-sm text-ink-secondary">
            {person.phone ? formatPhone(person.phone) : `@${person.handle}`}
            {presence ? ` · ${presence}` : ""}
          </p>
          <Button
            className="mt-[18px] self-stretch"
            size="lg"
            icon={<MessageIcon size={19} />}
            disabled={busy || person.is_blocked}
            onClick={() => void message()}
          >
            Message {first}
          </Button>
        </div>

        <div className="flex flex-col gap-3 px-4 pt-3.5 pb-6">
          {error && (
            <p role="alert" className="m-0 rounded-field bg-scarlet-tint px-3.5 py-2.5 text-sm text-scarlet">
              {error}
            </p>
          )}
          {person.about && (
            <Card className="px-4 py-3.5">
              <Eyebrow>About</Eyebrow>
              <div className="mt-1 text-[15px]">{person.about}</div>
            </Card>
          )}
          <SafetyNumbers userId={person.id} name={person.display_name} />
          <div className="flex flex-col gap-2">
            <Button variant="outline" className="h-12 rounded-tile" onClick={() => void toggleContact()}>
              {person.is_contact ? "Remove from contacts" : "Add to contacts"}
            </Button>
            <Button
              variant="danger"
              className="h-12 rounded-tile text-[15px]"
              onClick={() => void toggleBlock()}
            >
              {person.is_blocked ? `Unblock ${first}` : `Block ${first}`}
            </Button>
            <Button
              variant="ghost"
              className="self-center text-ink-secondary"
              onClick={() => setReporting(true)}
            >
              Report {first}
            </Button>
            {reported && (
              <p role="status" className="m-0 text-center text-[13px] text-ink-secondary">
                Thank you. We will look at your report.
              </p>
            )}
          </div>
          <ReportSheet
            open={reporting}
            onClose={(sent) => {
              setReporting(false);
              if (!sent) return;
              setReported(true);
              reload();
              contactsChanged();
            }}
            target={{ userId: person.id, ...(direct ? { chatId: direct.id } : {}), name: first }}
          />
        </div>
      </div>
    </div>
  );
}
